import jsep from 'jsep';
import { mapState } from 'vuex';
import { Operation, getOperationObject, getVisualTime, getVisualVersion, ops, sxsOps, timeOps } from '@/components/partials/FilterBuilder/helpers';
import { escapeTags } from '@/helpers/util';
import { Kpi } from '@/kpi';
import { ops as kpiOps } from '@/kpi/constants';
import { FILTERS } from '@/store/types/namespaces';

// FIXME: it is very surprising that a mixin will throw because a component
// doesn't have certain traits.
const noPropsMessage = 'The component useListableProperties to convert to a property';

const makeBold = (str) => `<span style="font-weight: 600;">${str}</span>`;

export function readableToHtml(description) {
  return description.map((line) => {
    const lineComponents = line.map((part) => {
      if (part.style === 'bold') {
        return makeBold(escapeTags(part.text));
      }
      return escapeTags(part.text);
    }).join(' ');
    const lineHtml = `<span>${lineComponents}</span>`;
    return lineHtml;
  }).join('<br>');
}

export function readableFilter(query, properties) {
  const visual = getVisualVersion(query, properties);

  if (visual.error) {
    // Escape advanced queries
    const NUMBER_OF_ADVANCED_CHARACTERS = 200;
    return [
      [
        {
          text: query.length > NUMBER_OF_ADVANCED_CHARACTERS
            ? `${query.substring(0, NUMBER_OF_ADVANCED_CHARACTERS)}...`
            : query,
          style: 'normal',
        },
      ],
    ];
  }

  const lines = visual.rows.map((v) => {
    let op = null;
    let isTimeOp = false;

    const combinedOps = {
      ...ops,
      ...timeOps,
    };
    Object.keys(combinedOps).forEach((k) => {
      const o = combinedOps[k];
      if (o.value === v.op) {
        op = o;

        if (timeOps[k] === o) {
          isTimeOp = true;
        }
      }
    });

    const displayAlias = (alias) => escapeTags(alias);
    let alias = null;
    if (v.left.alias) {
      alias = displayAlias(v.left.alias);
    }

    if (!op) {
      return [
        { text: alias },
        { text: v.op },
        { text: v.right },
      ];
    }

    if (op.type === Operation.COMPARISON) {
      const value = Number.isNaN(v.right) ? `"${v.right}"` : v.right;
      return [
        { text: alias, style: 'bold' },
        { text: op.text },
        { text: value },
      ];
    } if (op.type === Operation.NO_RIGHT) {
      return [
        { text: alias, style: 'bold' },
        { text: op.text },
      ];
    } if (op.type === Operation.FUNCTION) {
      const value = op.value.includes('valueStr') ? `"${v.right}"` : v.right;

      if (isTimeOp) {
        const { displayedTime, modifierLabel } = getVisualTime(value);
        return [
          { text: alias, style: 'bold' },
          { text: op.text },
          { text: displayedTime },
          { text: modifierLabel },
        ];
      }
      return [
        { text: alias, style: 'bold' },
        { text: op.text },
        { text: value },
      ];
    }
    return null;
  });

  const MAX_LINES = 5;

  let reduced = lines.filter((l) => l !== null);
  if (lines.length > MAX_LINES) {
    reduced = reduced.slice(0, MAX_LINES);

    const extraLines = lines.length - reduced.length;
    reduced.push([
      { text: `${extraLines} more rules...` },
    ]);
  }

  // Add the operators
  if (reduced.length > 1) {
    reduced = reduced.map((line, index) => {
      if (index === 0) return line;
      line.unshift({ text: visual.operator });
      return line;
    });
  }

  return reduced;
}

function getReadableMeasurements(kpi, properties) {
  const { measurements } = kpi;

  const measurementLines = measurements.map((m) => {
    const { expression, filter, property } = m;
    const operation = kpiOps.find((op) => op.value === m.op);
    const measurementLine = [
      { text: operation.text },
    ];
    if (expression) {
      const readableLines = readableFilter(expression, properties);
      const cleanText = readableLines.map((line) => {
        const joined = line
          .filter(({ text }) => text !== 'Advanced query:')
          .map(({ text }) => text).join(' ');
        return joined;
      }).join(' ');
      measurementLine.push({
        text: cleanText,
        style: 'bold',
      });
    } else if (filter) {
      measurementLine.push({
        text: filter.alias || filter.name,
        style: 'bold',
      });
    } else if (property) {
      measurementLine.push({
        text: property.alias,
        style: 'bold',
      });
    }
    return measurementLine;
  });

  return measurementLines;
}

export function readableKpi(kpi) {
  if (this.listableProperties === undefined) throw new Error(noPropsMessage);

  const kpiObj = Kpi.fromSaveObject(kpi, this.filters, this.listableProperties);

  const sentences = getReadableMeasurements(kpiObj, this.listableProperties);

  if (kpiObj.grouping || kpiObj.binSize) {
    const groupedText = [{ text: 'grouped by' }];
    if (kpiObj.grouping) {
      groupedText.push({ text: kpiObj.grouping.alias, style: 'bold' });
    }
    if (kpiObj.binSize) {
      if (kpiObj.grouping) {
        groupedText.push({ text: 'and' });
      }
      groupedText.push({ text: kpiObj.binSize, style: 'bold' });
    }
    sentences.push(groupedText);
  }

  if (kpiObj.scope) {
    sentences.push([
      { text: 'where' },
      {
        text: kpiObj.scope.name
          || readableFilter(kpiObj.scope.expression, this.listableProperties),
        style: 'bold',
      },
    ]);
  }

  return sentences;
}

function parseReadableRuleRow(row, ignoreLeft) {
  const { left, op, right } = row;
  let propertyText = '';
  let opText;

  if (left === 'change' || left === '"{change%}"' || left === 'change%'
    || left === 'deltaY' || left === 'deltaYPercent') {
    opText = op === '>'
      ? sxsOps.increasedByMoreThan.text
      : sxsOps.decreasedByMoreThan.text;
  } else {
    propertyText = typeof left === 'object' && !ignoreLeft ? left.alias : '';
    opText = getOperationObject(op).text;
  }
  const valueText = right;
  const isPercent = left === 'deltaYPercent' || left === 'change%' || left === '"{change%}"';
  return [
    { text: propertyText, style: 'bold' },
    { text: opText },
    { text: `${valueText}${isPercent ? '%' : ''}`.trim() },
  ];
}

export function getReadableRuleExpression(expression, properties, ignoreLeft) {
  const visual = getVisualVersion(expression, properties);
  let isPercent = false;

  if (!visual.error) {
    const lines = visual.rows.map((row) => parseReadableRuleRow(row, ignoreLeft));
    return lines.map((line, index) => {
      if (index !== 0) {
        return [
          { text: visual.operator },
          ...line,
        ];
      }
      return line;
    });
  }

  let parsed;
  try {
    parsed = jsep(expression);
  } catch (e) {
    return [[{ text: expression }]];
  }
  const { left, right } = parsed;
  if (!left || !left.callee || left.callee.name !== 'abs') return [[{ text: expression }]];

  const opText = sxsOps.changedByMoreThan.text;
  const valueText = right.value;
  const [arg] = left.arguments;
  isPercent = arg.name === 'deltaYPercent' || arg.name === 'change%' || arg.raw === '"{change%}"';

  return [
    { text: opText },
    { text: `${valueText}${isPercent ? '%' : ''}`.trim() },
  ];
}

export function readableRule(rule) {
  if (this.listableProperties === undefined) throw new Error(noPropsMessage);

  const { kpi, period, expression } = rule;

  if (!kpi) {
    return getReadableRuleExpression(expression, this.listableProperties);
  }

  const kpiObj = Kpi.fromSaveObject({ kpi }, this.filters, this.listableProperties);

  const sentences = getReadableMeasurements(kpiObj, this.listableProperties);

  if (period) {
    sentences.push([
      { text: 'over the last' },
      { text: period, style: 'bold' },
    ]);
  }

  if (kpiObj.grouping) {
    sentences.push([
      { text: 'grouped by' },
      { text: kpiObj.grouping.alias || kpiObj.grouping.name, style: 'bold' },
    ]);
  }

  if (kpiObj.scope) {
    sentences.push([
      { text: 'where' },
      {
        text: kpiObj.scope.name
          || readableFilter(kpiObj.scope.expression, this.listableProperties),
        style: 'bold',
      },
    ]);
  }

  sentences.push(getReadableRuleExpression(expression, this.listableProperties, true)[0]);

  return sentences;
}

export default {
  computed: {
    ...mapState(FILTERS, ['filters']),
  },
  methods: {
    readableFilter,
    readableKpi,
    readableRule,
    readableToHtml,
  },
};
